// src/services/consultation.service.ts
import { db } from '../config/firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  orderBy,
  Timestamp,
  where
} from 'firebase/firestore';
import {
  ConsultationRequest,
  ConsultationStatus,
  CreateConsultationRequest
} from '../types/consultation.types';

const COLLECTION_NAME = 'consultationRequests';

export const consultationService = {
  async createRequest(data: CreateConsultationRequest): Promise<string> {
    const docRef = doc(collection(db, COLLECTION_NAME));
    const request: ConsultationRequest = {
      id: docRef.id,
      name: data.name,
      email: data.email,
      organization: data.organization,
      message: data.message,
      status: 'new',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    await setDoc(docRef, request);
    return docRef.id;
  },

  async getAllRequests(): Promise<ConsultationRequest[]> {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as ConsultationRequest);
  },

  async getRequestsByStatus(status: ConsultationStatus): Promise<ConsultationRequest[]> {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as ConsultationRequest);
  },

  async getRequest(requestId: string): Promise<ConsultationRequest | null> {
    const docRef = doc(db, COLLECTION_NAME, requestId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as ConsultationRequest;
    }
    return null;
  },

  async updateStatus(
    requestId: string,
    status: ConsultationStatus,
    notes?: string
  ): Promise<void> {
    const updates: Partial<ConsultationRequest> = {
      status,
      updatedAt: Timestamp.now()
    };

    if (notes) {
      updates.notes = notes;
    }

    // Add timestamp for specific status changes
    if (status === 'contacted') {
      updates.contactedAt = Timestamp.now();
    } else if (status === 'scheduled') {
      updates.scheduledAt = Timestamp.now();
    } else if (status === 'completed') {
      updates.completedAt = Timestamp.now();
    }

    await updateDoc(doc(db, COLLECTION_NAME, requestId), updates);
  },

  async updateRequest(
    requestId: string,
    updates: Partial<ConsultationRequest>
  ): Promise<void> {
    await updateDoc(doc(db, COLLECTION_NAME, requestId), {
      ...updates,
      updatedAt: Timestamp.now()
    });
  },

  async getNewRequestsCount(): Promise<number> {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('status', '==', 'new')
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  }
};
